import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import { API_URL, normalizeList } from '../config';
import './AdminAttributesPage.css';

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SIZE_CATEGORIES = ['Clothing', 'Men & Women', 'Kids Wear', 'Waist', 'Footwear', 'One Size', 'Other'];

const getAttrError = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.message || data.title || fallback;
};

const AdminAttributesPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);

  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [addSizeName, setAddSizeName] = useState('');
  const [addSizeCategory, setAddSizeCategory] = useState('Clothing');
  const [addColorName, setAddColorName] = useState('');
  const [addColorHex, setAddColorHex] = useState('#000000');
  const [savingSize, setSavingSize] = useState(false);
  const [savingColor, setSavingColor] = useState(false);

  const [editingSizeId, setEditingSizeId] = useState(null);
  const [editSizeName, setEditSizeName] = useState('');
  const [editSizeCategory, setEditSizeCategory] = useState('');
  const [editingColorId, setEditingColorId] = useState(null);
  const [editColorName, setEditColorName] = useState('');
  const [editColorHex, setEditColorHex] = useState('');

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Admin') {
      navigate('/login');
      return;
    }
    fetchAll();
  }, [isLoggedIn, userRole, navigate]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [sizesRes, colorsRes] = await Promise.all([
        axios.get(`${API_URL}/Sizes?includeDeleted=true`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/Colors?includeDeleted=true`, { headers: getAuthHeader() }),
      ]);
      setSizes(normalizeList(sizesRes.data));
      setColors(normalizeList(colorsRes.data));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load sizes and colors.' });
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const normalizeHex = (v) => {
    if (!v || typeof v !== 'string') return '';
    const s = v.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
    if (/^[0-9A-Fa-f]{6}$/.test(s)) return '#' + s;
    return s;
  };

  const handleAddSize = async (e) => {
    e.preventDefault();
    if (!addSizeName.trim()) return;
    setSavingSize(true);
    try {
      await axios.post(
        `${API_URL}/Sizes`,
        { sizeName: addSizeName.trim(), sizeCategory: addSizeCategory || null },
        { headers: getAuthHeader() }
      );
      setAddSizeName('');
      setAddSizeCategory('Clothing');
      fetchAll();
      showMsg('success', 'Size added.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to add size.'));
    } finally {
      setSavingSize(false);
    }
  };

  const handleAddColor = async (e) => {
    e.preventDefault();
    if (!addColorName.trim()) return;
    const hex = normalizeHex(addColorHex);
    if (hex && !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      showMsg('error', 'Color code must be a valid hex (e.g. #FF5733).');
      return;
    }
    setSavingColor(true);
    try {
      await axios.post(
        `${API_URL}/Colors`,
        { colorName: addColorName.trim(), hexCode: hex || undefined },
        { headers: getAuthHeader() }
      );
      setAddColorName('');
      setAddColorHex('#000000');
      fetchAll();
      showMsg('success', 'Color added.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to add color.'));
    } finally {
      setSavingColor(false);
    }
  };

  const startEditSize = (s) => {
    setEditingSizeId(s.sizeId);
    setEditSizeName(s.sizeName);
    setEditSizeCategory(s.sizeCategory || '');
  };

  const startEditColor = (c) => {
    setEditingColorId(c.colorId);
    setEditColorName(c.colorName);
    setEditColorHex(c.hexCode || '#000000');
  };

  const handleUpdateSize = async () => {
    if (!editSizeName.trim()) return;
    try {
      await axios.put(
        `${API_URL}/Sizes/${editingSizeId}`,
        { sizeName: editSizeName.trim(), sizeCategory: editSizeCategory.trim() || null },
        { headers: getAuthHeader() }
      );
      setEditingSizeId(null);
      fetchAll();
      showMsg('success', 'Size updated.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to update size.'));
    }
  };

  const handleUpdateColor = async () => {
    if (!editColorName.trim()) return;
    const hex = normalizeHex(editColorHex);
    if (hex && !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      showMsg('error', 'Color code must be a valid hex.');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/Colors/${editingColorId}`,
        { colorName: editColorName.trim(), hexCode: hex || null },
        { headers: getAuthHeader() }
      );
      setEditingColorId(null);
      fetchAll();
      showMsg('success', 'Color updated.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to update color.'));
    }
  };

  const handleDeleteSize = async (id) => {
    if (!window.confirm('Archive this size? It will be hidden from new products. Existing products keep using it.')) return;
    try {
      await axios.delete(`${API_URL}/Sizes/${id}`, { headers: getAuthHeader() });
      fetchAll();
      showMsg('success', 'Size archived.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to archive size.'));
    }
  };

  const handleRestoreSize = async (id) => {
    try {
      await axios.post(`${API_URL}/Sizes/${id}/restore`, {}, { headers: getAuthHeader() });
      fetchAll();
      showMsg('success', 'Size restored.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to restore size.'));
    }
  };

  const handleDeleteColor = async (id) => {
    if (!window.confirm('Archive this color? It will be hidden from new products. Existing products keep using it.')) return;
    try {
      await axios.delete(`${API_URL}/Colors/${id}`, { headers: getAuthHeader() });
      fetchAll();
      showMsg('success', 'Color archived.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to archive color.'));
    }
  };

  const handleRestoreColor = async (id) => {
    try {
      await axios.post(`${API_URL}/Colors/${id}/restore`, {}, { headers: getAuthHeader() });
      fetchAll();
      showMsg('success', 'Color restored.');
    } catch (err) {
      showMsg('error', getAttrError(err, 'Failed to restore color.'));
    }
  };

  if (!isLoggedIn || userRole !== 'Admin') return null;

  return (
    <div className="admin-attributes-wrapper">
      <Header />
      <div className="admin-attributes-container">
        <h1>Sizes &amp; Colors</h1>
        <p className="page-subtitle">Manage product attributes used for Clothes/Garments. Archive hides a size or color from new products without removing it from existing ones. Restore brings it back.</p>

        {message.text && (
          <div className={`admin-attributes-msg ${message.type}`}>{message.text}</div>
        )}

        {loading ? (
          <p className="loading">Loading...</p>
        ) : (
          <>
            <section className="attributes-section">
              <h2>Sizes</h2>
              <form onSubmit={handleAddSize} className="attributes-add-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Size name (e.g. S, M, L, 32)"
                    value={addSizeName}
                    onChange={(e) => setAddSizeName(e.target.value)}
                    maxLength={50}
                    className="input-name"
                  />
                  <select
                    value={addSizeCategory}
                    onChange={(e) => setAddSizeCategory(e.target.value)}
                    className="input-category"
                  >
                    {SIZE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button type="submit" className="btn-add" disabled={savingSize || !addSizeName.trim()}>
                    {savingSize ? 'Adding...' : 'Add Size'}
                  </button>
                </div>
              </form>
              <div className="attributes-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Size name</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.length === 0 ? (
                      <tr><td colSpan="4" className="no-data">No sizes yet. Add one above.</td></tr>
                    ) : (
                      sizes.map((s) => (
                        <tr key={s.sizeId} className={s.isDeleted ? 'row-archived' : ''}>
                          {editingSizeId === s.sizeId ? (
                            <>
                              <td>
                                <input
                                  type="text"
                                  value={editSizeName}
                                  onChange={(e) => setEditSizeName(e.target.value)}
                                  className="edit-input"
                                  maxLength={50}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editSizeCategory}
                                  onChange={(e) => setEditSizeCategory(e.target.value)}
                                  placeholder="e.g. Clothing"
                                  className="edit-input"
                                  maxLength={50}
                                />
                              </td>
                              <td><span className="status-active">Active</span></td>
                              <td>
                                <button type="button" className="btn-save" onClick={handleUpdateSize}>Save</button>
                                <button type="button" className="btn-cancel" onClick={() => setEditingSizeId(null)}>Cancel</button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{s.sizeName}</td>
                              <td>{s.sizeCategory || '–'}</td>
                              <td>{s.isDeleted ? <span className="status-archived">Archived</span> : <span className="status-active">Active</span>}</td>
                              <td>
                                {s.isDeleted ? (
                                  <button type="button" className="btn-restore" onClick={() => handleRestoreSize(s.sizeId)}>Restore</button>
                                ) : (
                                  <>
                                    <button type="button" className="btn-edit" onClick={() => startEditSize(s)}>Edit</button>
                                    <button type="button" className="btn-delete" onClick={() => handleDeleteSize(s.sizeId)}>Archive</button>
                                  </>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="attributes-section">
              <h2>Colors</h2>
              <form onSubmit={handleAddColor} className="attributes-add-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Color name (e.g. Navy Blue)"
                    value={addColorName}
                    onChange={(e) => setAddColorName(e.target.value)}
                    maxLength={100}
                    className="input-name"
                  />
                  <div className="color-code-wrap">
                    <input
                      type="color"
                      value={addColorHex}
                      onChange={(e) => setAddColorHex(e.target.value)}
                      className="input-color-picker"
                      title="Pick color"
                    />
                    <input
                      type="text"
                      placeholder="#FF5733"
                      value={addColorHex}
                      onChange={(e) => setAddColorHex(e.target.value)}
                      className="input-hex"
                      maxLength={7}
                    />
                  </div>
                  <button type="submit" className="btn-add" disabled={savingColor || !addColorName.trim()}>
                    {savingColor ? 'Adding...' : 'Add Color'}
                  </button>
                </div>
              </form>
              <div className="attributes-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Swatch</th>
                      <th>Color name</th>
                      <th>Color code</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colors.length === 0 ? (
                      <tr><td colSpan="5" className="no-data">No colors yet. Add one above.</td></tr>
                    ) : (
                      colors.map((c) => (
                        <tr key={c.colorId} className={c.isDeleted ? 'row-archived' : ''}>
                          {editingColorId === c.colorId ? (
                            <>
                              <td>
                                <input
                                  type="color"
                                  value={editColorHex}
                                  onChange={(e) => setEditColorHex(e.target.value)}
                                  className="edit-color-picker"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editColorName}
                                  onChange={(e) => setEditColorName(e.target.value)}
                                  className="edit-input"
                                  maxLength={100}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={editColorHex}
                                  onChange={(e) => setEditColorHex(e.target.value)}
                                  placeholder="#000000"
                                  className="edit-input edit-hex"
                                  maxLength={7}
                                />
                              </td>
                              <td><span className="status-active">Active</span></td>
                              <td>
                                <button type="button" className="btn-save" onClick={handleUpdateColor}>Save</button>
                                <button type="button" className="btn-cancel" onClick={() => setEditingColorId(null)}>Cancel</button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <span
                                  className="color-swatch"
                                  style={{ backgroundColor: c.hexCode || '#e5e7eb' }}
                                  title={c.hexCode || ''}
                                />
                              </td>
                              <td>{c.colorName}</td>
                              <td>{c.hexCode || '–'}</td>
                              <td>{c.isDeleted ? <span className="status-archived">Archived</span> : <span className="status-active">Active</span>}</td>
                              <td>
                                {c.isDeleted ? (
                                  <button type="button" className="btn-restore" onClick={() => handleRestoreColor(c.colorId)}>Restore</button>
                                ) : (
                                  <>
                                    <button type="button" className="btn-edit" onClick={() => startEditColor(c)}>Edit</button>
                                    <button type="button" className="btn-delete" onClick={() => handleDeleteColor(c.colorId)}>Archive</button>
                                  </>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminAttributesPage;
